/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { observer } from "mobx-react-lite"

import { UserAppTypeViewModel } from "../../features/user/user-apptype-viewmodel"
import { UserDetailViewModel } from "../../features/user/user-detail-view-model"
import { useViewModel, useViewModelWithArgs } from "../app/app-hooks"
import { ApplicationTypeRowView } from "../application-type/application-type-row-view"

interface AssignedApplicationsCardProps {
    userId: number
    orgId: number
    onEditClick: () => void
}

export const AssignedApplicationsCard = observer(({ userId, orgId, onEditClick }: AssignedApplicationsCardProps) => {
    const vm = useViewModel(UserAppTypeViewModel)
    const userVM = useViewModelWithArgs(UserDetailViewModel, userId)
    const appTypes = vm.userAppTypesForOrganization(userId, orgId)
    const userAppTypesState = vm.getUserAppTypesFetchState()

    return (
        <div className="row g-4 mt-0">
            <div className="col-sm-12">
                <div className="card shadow-legacy-sm">
                    <div className="card-header custom-card-header flex justify-between items-center">
                        <h5 className="mb-0">Assigned Applications</h5>

                        {userVM.userDetail?.enabled && (
                            <button type="button" className="btn btn-light btn-sm" onClick={onEditClick}>
                                Edit
                            </button>
                        )}
                    </div>
                    <table className="table">
                        <tbody>
                            {userAppTypesState === "not-fetched" && renderPlaceholder()}
                            {userAppTypesState === "empty" && renderEmptyApps()}
                            {userAppTypesState === "has-app-types" &&
                                appTypes.map((appType) => (
                                    <ApplicationTypeRowView
                                        selected={false}
                                        selectable={false}
                                        appType={appType}
                                        key={appType.id ?? 0}
                                    />
                                ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    )
})

function renderPlaceholder(itemsCount: number = 3) {
    return [...Array(itemsCount)]
        .map((_, i) => `placeholder-${i}`)
        .map((key) => (
            <tr key={key} className="placeholder-glow">
                <td>
                    <span className="placeholder col-2 bg-legacy-secondary"></span>
                </td>
                <td>
                    <span className="placeholder col-4 bg-legacy-secondary"></span>
                </td>
                <td>
                    <span className="placeholder col-6 bg-legacy-secondary"></span>
                </td>
                <td>
                    <span className="placeholder col-4 bg-legacy-secondary"></span>
                </td>
            </tr>
        ))
}

function renderEmptyApps() {
    return (
        <tr>
            <td colSpan={4} className="text-center py-legacy">
                No applications assigned to this user in this organization.
            </td>
        </tr>
    )
}
